import { useCallback, useMemo, useState } from 'react';
import { Edit2 } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { EditPatchTypeHeader } from './components/EditPatchTypeHeader/EditPatchTypeHeader';
import { Card } from '../Card';
import { useYesNoTranslation } from '@/common/hooks/useYesNoTranslation/UseYesNoTranslation';
import { PatchTypeProps } from './PatchType.types';
import { BundleUpcharge, Patch, PatchTypeData } from '@/types/bundles-upcharges.types';
import Table from '@/components/Tables/Table/Table';
import { SwitchCell } from '@/components/SwitchCell/SwitchCell';
import { Box, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';

import styles from './PatchType.module.css';

export const PatchType = ({
  bundleUpcharges,
  bundleUpchargeSelected,
  descriptionAlreadyExists = false,
  onEdit,
  onCancel,
  onToggleLock,
  onSave,
  onHandleDescriptionChange,
}: PatchTypeProps) => {
  const { t } = useTranslation();
  const { yesNoTranslation } = useYesNoTranslation();
  const [patchTypeData, setPatchTypeData] = useState<Patch[]>(bundleUpcharges?.patches || []);

  const isTheSameBundleUpcharge = bundleUpchargeSelected?.type === bundleUpcharges?.type;
  const isNew = isTheSameBundleUpcharge ? false : !bundleUpcharges?.description;

  const patchesData: PatchTypeData[] = useMemo(
    () =>
      bundleUpcharges?.patches.map((patch) => ({
        ...patch,
        isVisible: yesNoTranslation(Boolean(patch.isVisible)),
      })),
    [bundleUpcharges?.patches, yesNoTranslation],
  );

  const renderIsVisibleCell = useMemo(() => {
    if (isTheSameBundleUpcharge) {
      return SwitchCell;
    }

    return ({ row }: CellContext<PatchTypeData, unknown>) => row.original.isVisible;
  }, [isTheSameBundleUpcharge]);

  const columns = useMemo(
    () => [
      {
        header: t('bundleUpcharges.patchTable.patchName'),
        accessorKey: 'name',
        enableSorting: false,
      },
      {
        cell: renderIsVisibleCell,
        header: t('bundleUpcharges.patchTable.isVisible'),
        accessorKey: 'isVisible',
        enableSorting: false,
      },
    ],
    [renderIsVisibleCell, t],
  );

  const handleEdit = useCallback(() => {
    onEdit(bundleUpcharges);
  }, [bundleUpcharges, onEdit]);

  const handleSave = useCallback(() => {
    const data: BundleUpcharge = {
      ...bundleUpchargeSelected,
      description: bundleUpchargeSelected?.description || '',
      isLocked: Boolean(bundleUpchargeSelected?.isLocked),
      type: String(bundleUpchargeSelected?.type),
      patches: patchTypeData,
    };
    onSave(data);
  }, [bundleUpchargeSelected, onSave, patchTypeData]);

  return (
    <Card.Root className={styles.cardWidth} isNew={isNew}>
      <Card.Header>
        {bundleUpchargeSelected?.type === bundleUpcharges.type ? (
          <EditPatchTypeHeader
            bundleUpchargeSelected={bundleUpchargeSelected}
            onCancel={onCancel}
            onToggleLock={onToggleLock}
            onSave={handleSave}
            onHandleDescriptionChange={onHandleDescriptionChange}
            descriptionAlreadyExists={descriptionAlreadyExists}
          />
        ) : (
          <>
            <Box display="flex" gap={2} alignItems="center">
              <Card.Title title={bundleUpcharges.description} />
              {bundleUpcharges.isLocked && (
                <Box
                  display="flex"
                  gap={0.5}
                  alignItems="center"
                  fontSize={24}
                  paddingY={0.25}
                  paddingX={1.5}
                  bgcolor="var(--red-100)"
                  borderRadius={8}
                  border="1px solid var(--red-700)"
                >
                  <LockOutlined fontSize="large" />
                  <Typography variant="subtitle1" fontSize={12} fontFamily="Open Sans" color="#252525">
                    {t('bundleUpcharges.locked')}
                  </Typography>
                </Box>
              )}
            </Box>
            <Card.Action icon={Edit2} title={t('common.edit')} onClick={handleEdit} />
          </>
        )}
      </Card.Header>
      <Card.Content>
        <Table
          className={styles.cardWidth}
          includeMinWidth={false}
          isLoading={false}
          data={patchesData}
          meta={{
            columns,
            data: patchesData,
            updateData(rowIndex, columnId, value) {
              setPatchTypeData((old) =>
                old.map((row, index) => {
                  if (index === rowIndex) {
                    return {
                      ...old[rowIndex],
                      [columnId]: value,
                    };
                  }
                  return row;
                }),
              );
            },
          }}
          columns={columns}
        />
      </Card.Content>
    </Card.Root>
  );
};
